import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {InfluencerPost} from '../../../../models/influencer';
import {Masonry} from '../../../../../node_modules/ng-masonry-grid';
import {PdfExportService} from '../../../services/export-pdf/pdf-export.service';
import {PostImageComponent} from './post-image/post-image.component';

enum CssEnum {
  HIDDEN = 'hidden',
  VISIBLE = 'visible',
  MAX_CONTENT = 'max-content'
}

enum ShowMore {
  MIN_POST = 10
}

@Component({
  selector: 'app-post-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './post-list.component.html',
  styleUrls: ['./post-list.component.scss']
})
export class PostListComponent implements OnInit {
  static COMPONENT_NAME = 'PostListComponent';
  @ViewChild('popup')
  public popup: ElementRef;
  @ViewChild('popupcontainer')
  public popupcontainer: ElementRef;
  @ViewChild('postListContainer')
  public postListContainer: ElementRef;
  @ViewChildren(PostImageComponent)
  postItems: QueryList<PostImageComponent>;

  @Input() postList: Array<InfluencerPost>;
  @Input() influencerGroupId: number;
  @Input() showMoreheight: string;
  @Input() previewMode: boolean | false;
  @Input() target: string;
  @Input() displayMode = 'desktop';
  @Output()
  public selectToPreviewPost: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  public openIncompleteStory: EventEmitter<any> = new EventEmitter<any>();
  @Input()
  bottomBar: any;
  @Input()
  columnWidth: any;
  @Input()
  gutter: number;
  @Input()
  keepOrder = false;
  @Output()
  public isLayoutCompleted: EventEmitter<any> = new EventEmitter<any>();

  public activePost = null;

  public height: string;
  public overflow: string;
  private keepHoverPopup: boolean;
  public openPreviewPostPopup = false;

  _masonry: Masonry;
  private animateHandler: number;

  constructor(private pdfService: PdfExportService, private _ngZone: NgZone, private cd: ChangeDetectorRef) {

  }

  ngOnInit() {
    if (this.postList && this.postList.length > ShowMore.MIN_POST) {
      this.height = this.showMoreheight;
      this.overflow = CssEnum.HIDDEN;
    } else {
      this.height = CssEnum.MAX_CONTENT;
      this.overflow = CssEnum.VISIBLE;
    }
  }

  // Get ng masonry grid instance first
  onNgMasonryInit($event: Masonry) {
    this._masonry = $event;
  }

  layoutComplete (laidOutItems ) {
    if (this._masonry) {
      if (laidOutItems && this._masonry.items.length === this.postList.length) {
        this.pdfService.incrementNumberReportReady(PostListComponent.COMPONENT_NAME);
      }

      if (Array.isArray(laidOutItems) && laidOutItems.length === this.postList.length) {
        this.isLayoutCompleted.emit(true);
      }
    }
  }

  showMore() {
    this.height = CssEnum.MAX_CONTENT;
    this.overflow = CssEnum.VISIBLE;
  }

  prependItems(newItems) {
    if (this._masonry) {
      // set status to 'prepend' before adding items to NgMasonryGrid otherwise default: 'append' will applied
      this._masonry.setAddStatus('prepend');
      this.postList.splice(0, 0, ...newItems);
    }
  }

  onMouseHover(e, postId, postItem) {
    this.activePost = postItem;
    this.keepHoverPopup = false;
    this.openPreviewPostPopup = true;
    this.cd.detectChanges();
    setTimeout(() => {
      this.showPreviewPopup(postId, postItem);
    }, 100);

  }

  private showPreviewPopup(postId, postItem) {
    /**
     * Four following lines of code is used fixing bug https://www.pivotaltracker.com/story/show/172276017
     * Post is shown when layout complete
     */
    const filterdPostItem = this._masonry.items.find(item => item.element.id === postId);
    if (!filterdPostItem) {
      return;
    }
    // Create new modal from template
    const container = document.getElementById('popupcontainer');
    // post list container
    const postListContainerPos = this.postListContainer.nativeElement.getBoundingClientRect();
    // find active card element
    const element = this.postItems.find(post => post.post.urlHash === postItem.urlHash);
    // get native element of active card
    const postContentInnerEle = element.postImageContainer.nativeElement;
    // define margin left
    const marginLeft = 30;
    // define bottom bar height
    const bottomBarHeight = this.bottomBar ? this.bottomBar.clientHeight : 100;


    // Set fields

    // specific handler for video player
    const storyVideoPlayer = this.popup.nativeElement.querySelector('.video-player-container');
    let videoHeight = 0;
    if (storyVideoPlayer && postItem.isVideo && postItem.completed) {
      videoHeight = 260 * (postItem.mediaHeight / postItem.mediaWidth);
    }
    // on mobile not need Calculate position
    if (this.displayMode === 'mobile') {
      return;
    }

    const windowHeight = document.documentElement.clientHeight - bottomBarHeight;

    // Calculate position to center over target
    let left = postContentInnerEle.offsetParent.offsetLeft - (this.popup.nativeElement.scrollWidth / 2) + (postContentInnerEle.scrollWidth / 2) + marginLeft * 2;
    let top;
    if (windowHeight < container.offsetHeight || this.popup.nativeElement.scrollHeight < container.offsetHeight) {
      top = postContentInnerEle.offsetParent.offsetTop - (this.popup.nativeElement.scrollHeight / 2) + (postContentInnerEle.scrollHeight / 2);
    } else {
      top = 0 - (this.popup.nativeElement.scrollHeight / 2) + (postContentInnerEle.scrollHeight / 2);
    }

    // Make sure we don't grow beyond window
    const containerOffset = this.getOffsetToWindow(container);
    // a special case with story, at the first time, popup height will not include the video height, due to video not loaded yet
    // so we need to add the video height with current popup
    const popupHeight = this.popup.nativeElement.scrollHeight < videoHeight
      ? this.popup.nativeElement.scrollHeight + videoHeight : this.popup.nativeElement.scrollHeight;
    const popupPos = {
      left: containerOffset.left + left,
      top: top,
      right: containerOffset.left + left + this.popup.nativeElement.scrollWidth,
      bottom: top + popupHeight
    };

    /**
     * when post list is collapped. Use the window as the limited box
     * when post list is expanded. Use Post list container as the limited box
     */
    let limitTop = 30;
    let limitBottom = postListContainerPos.bottom;

    if ((limitTop + windowHeight) < postListContainerPos.bottom) {
      limitBottom = limitTop + windowHeight;
      top = postContentInnerEle.offsetParent.offsetTop - (this.popup.nativeElement.scrollHeight / 2) + (postContentInnerEle.scrollHeight / 2);
    }

    limitTop = 30 - postListContainerPos.top;
    limitBottom = limitBottom - postListContainerPos.top;

    const limits = {
      left: 10,
      top: limitTop,
      right: document.documentElement.clientWidth - 10,
      bottom: limitBottom
    };

    // Offset position if any limits are hit
    if (popupPos.left < limits.left) {
      left += limits.left - popupPos.left;
    }

    if (popupPos.right > limits.right) {
      left -= popupPos.right - limits.right;
    }

    if (popupPos.top < limits.top) {
      top += limits.top - popupPos.top;
    } else if (popupPos.bottom > limits.bottom) {
      top -= popupPos.bottom - limits.bottom;
    }

    // Set position
    this.popup.nativeElement.style.left = left + 'px';
    this.popup.nativeElement.style.top = top + 'px';
    this.popup.nativeElement.classList.add('show');
  }

  private onToggleMediaPlayer(mediaPlayer) {
    const video = mediaPlayer.querySelector('video');
    if (video.paused) {
      mediaPlayer.classList.add('playing');
      video.play();
    } else {
      mediaPlayer.classList.remove('playing');
      video.pause();
    }
  }

  private getOffsetToWindow(_el) {
    const target = _el,
      targetWidth = target.offsetWidth,
      targetHeight = target.offsetHeight,
      targetLeft = target.offsetLeft,
      targetTop = target.offsetTop;

    let gleft = 0,
        gtop = 0,
        rect = {left: 0, top: 0, bottom: 0, right: 0};

    /**
     * this is a recursive method use to calculate the offset position of current element with Window
     * @param _parent
     * @returns {{top: any; left: any; bottom: any; right: any}}
     */
    const calculateOffsetWindowRecursive = ( _parent) => {
      if (!!_parent) {
        gleft += _parent.offsetLeft;
        gtop += _parent.offsetTop;
        calculateOffsetWindowRecursive(_parent.offsetParent);
      } else {
        return rect = {
          top: target.offsetTop + gtop,
          left: target.offsetLeft + gleft,
          bottom: (target.offsetTop + gtop) + targetHeight,
          right: (target.offsetLeft + gleft) + targetWidth
        };
      }
    };

    calculateOffsetWindowRecursive(target.offsetParent);
    return rect;
  }

  onSelectedPost(influencer: InfluencerPost) {
    this.selectToPreviewPost.emit(influencer);
    this.hidePopupCardHover();
  }

  addToPreviewed(item) {
    this.selectToPreviewPost.emit(item);
  }

  public hidePopupCardHover(evt?) {
    const clickingOnImage = evt && evt.target && evt.target.classList.length > 0 && evt.target.classList.contains('post-img');
    if (this.popup && !this.keepHoverPopup && !clickingOnImage) {
      this.popup.nativeElement.classList.remove('show');
      this.openPreviewPostPopup = false;
    }
  }

  trackByFn(index, item) {
    return index;
  }
}



// WEBPACK FOOTER //
// ./src/app/main/influencer/post-list/post-list.component.ts